// File: src/components/dashboard/GoalSettingTab.js
// This component is upgraded with more detailed goal-setting options.
import React, { useState, useEffect } from 'react';

function GoalSettingTab({ user, userSettings, goals, onSaveGoals }) {
  const [editableGoals, setEditableGoals] = useState(goals);
  const { unitSystem } = userSettings;

  useEffect(() => {
    setEditableGoals(goals);
  }, [goals]);

  const handleInputChange = (category, field, value) => {
    setEditableGoals(prev => ({ ...prev, [category]: { ...prev[category], [field]: value ? (typeof value === 'string' && value.includes('-') ? value : parseFloat(value)) : '' } }));
  };

  const handleDynamicChange = (category, index, field, value) => {
    const updatedArray = [...editableGoals[category]];
    updatedArray[index] = { ...updatedArray[index], [field]: value };
    setEditableGoals(prev => ({ ...prev, [category]: updatedArray }));
  };

  const addGoal = (category) => {
    const newGoal = category === 'performance' 
        ? { id: Date.now(), exercise: '', weight: '', targetDate: '' }
        : { id: Date.now(), exercise: '', metric: 'distance', value: '', time: '', targetDate: '' };
    setEditableGoals(prev => ({ ...prev, [category]: [...prev[category], newGoal] }));
  };

  const removeGoal = (category, index) => {
    const updatedArray = editableGoals[category].filter((_, i) => i !== index);
    setEditableGoals(prev => ({ ...prev, [category]: updatedArray }));
  };

  const GoalInput = ({ value, onChange, label, unit, type = "number" }) => (
    <div>
      <label className="block text-gray-400 text-sm font-bold mb-2">{label}</label>
      <div className="flex items-center">
        <input type={type} value={value || ''} onChange={onChange} className="appearance-none block w-full bg-gray-700 text-white border border-gray-600 rounded-l py-3 px-4 leading-tight focus:outline-none focus:bg-gray-600 focus:border-magenta" />
        {unit && <span className="bg-gray-600 text-white px-4 py-3 rounded-r border-r border-t border-b border-gray-600">{unit}</span>}
      </div>
    </div>
  );

  if (!editableGoals || !editableGoals.body || !editableGoals.nutrition || !editableGoals.performance) {
    return <div className="text-center text-gray-400">Loading goals...</div>;
  }

  return (
    <>
      <h3 className="text-3xl font-bold mb-6 text-lime">Set Your Goals</h3>
      <div className="space-y-8">
        
        <div className="bg-gray-900 p-6 rounded-lg">
          <h4 className="text-xl font-bold mb-4 text-magenta">Body Composition</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <GoalInput value={editableGoals.body.weight} onChange={(e) => handleInputChange('body', 'weight', e.target.value)} label="Target Weight" unit={unitSystem === 'metric' ? 'kg' : 'lbs'} />
            <GoalInput value={editableGoals.body.bodyFat} onChange={(e) => handleInputChange('body', 'bodyFat', e.target.value)} label="Target Body Fat" unit="%" />
            <GoalInput value={editableGoals.body.targetDate} onChange={(e) => handleInputChange('body', 'targetDate', e.target.value)} label="Target Date" type="date" />
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg">
          <h4 className="text-xl font-bold mb-4 text-magenta">Body Measurements</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <GoalInput value={editableGoals.measurements.waist} onChange={(e) => handleInputChange('measurements', 'waist', e.target.value)} label="Waist" unit={unitSystem === 'metric' ? 'cm' : 'in'} />
            <GoalInput value={editableGoals.measurements.chest} onChange={(e) => handleInputChange('measurements', 'chest', e.target.value)} label="Chest" unit={unitSystem === 'metric' ? 'cm' : 'in'} />
            <GoalInput value={editableGoals.measurements.hips} onChange={(e) => handleInputChange('measurements', 'hips', e.target.value)} label="Hips" unit={unitSystem === 'metric' ? 'cm' : 'in'} />
            <GoalInput value={editableGoals.measurements.arms} onChange={(e) => handleInputChange('measurements', 'arms', e.target.value)} label="Arms" unit={unitSystem === 'metric' ? 'cm' : 'in'} />
            <GoalInput value={editableGoals.measurements.targetDate} onChange={(e) => handleInputChange('measurements', 'targetDate', e.target.value)} label="Target Date" type="date" />
          </div>
        </div>
        
        <div className="bg-gray-900 p-6 rounded-lg">
          <h4 className="text-xl font-bold mb-4 text-magenta">Daily Nutrition</h4>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <GoalInput value={editableGoals.nutrition.calories} onChange={(e) => handleInputChange('nutrition', 'calories', e.target.value)} label="Target Calories" unit="kcal" />
            <GoalInput value={editableGoals.nutrition.protein} onChange={(e) => handleInputChange('nutrition', 'protein', e.target.value)} label="Target Protein" unit="g" />
            <GoalInput value={editableGoals.nutrition.carbs} onChange={(e) => handleInputChange('nutrition', 'carbs', e.target.value)} label="Target Carbs" unit="g" />
            <GoalInput value={editableGoals.nutrition.fat} onChange={(e) => handleInputChange('nutrition', 'fat', e.target.value)} label="Target Fat" unit="g" />
          </div>
        </div>

        <div className="bg-gray-900 p-6 rounded-lg">
          <h4 className="text-xl font-bold mb-4 text-magenta">Performance Lifts</h4>
          <div className="space-y-4">
            {editableGoals.performance.map((goal, index) => (
              <div key={goal.id} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <GoalInput value={goal.exercise} onChange={(e) => handleDynamicChange('performance', index, 'exercise', e.target.value)} label="Exercise" type="text" />
                <GoalInput value={goal.weight} onChange={(e) => handleDynamicChange('performance', index, 'weight', e.target.value)} label="Target Weight" unit={unitSystem === 'metric' ? 'kg' : 'lbs'} />
                <GoalInput value={goal.targetDate} onChange={(e) => handleDynamicChange('performance', index, 'targetDate', e.target.value)} label="Target Date" type="date" />
                <button onClick={() => removeGoal('performance', index)} className="bg-red-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-red-700 h-12">Remove</button>
              </div>
            ))}
          </div>
          <button onClick={() => addGoal('performance')} className="mt-4 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg">+ Add Performance Goal</button>
        </div>
        
        <div className="bg-gray-900 p-6 rounded-lg">
          <h4 className="text-xl font-bold mb-4 text-magenta">Cardio Goals</h4>
          <div className="space-y-4">
            {editableGoals.cardio.map((goal, index) => (
              <div key={goal.id} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                <div className="md:col-span-2"><GoalInput value={goal.exercise} onChange={(e) => handleDynamicChange('cardio', index, 'exercise', e.target.value)} label="Exercise" type="text" /></div>
                <div><label className="block text-gray-400 text-sm font-bold mb-2">Metric</label><select value={goal.metric} onChange={(e) => handleDynamicChange('cardio', index, 'metric', e.target.value)} className="w-full bg-gray-700 text-white border border-gray-600 rounded py-3 px-4 h-12"><option value="distance">Distance</option><option value="duration">Duration</option></select></div>
                <GoalInput value={goal.value} onChange={(e) => handleDynamicChange('cardio', index, 'value', e.target.value)} label="Target" unit={goal.metric === 'distance' ? (unitSystem === 'metric' ? 'km' : 'mi') : 'min'} />
                <GoalInput value={goal.time} onChange={(e) => handleDynamicChange('cardio', index, 'time', e.target.value)} label="In (mins)" unit="min" />
                <button onClick={() => removeGoal('cardio', index)} className="bg-red-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-red-700 h-12">Remove</button>
              </div>
            ))}
          </div>
          <button onClick={() => addGoal('cardio')} className="mt-4 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg">+ Add Cardio Goal</button>
        </div>

      </div>
      <div className="mt-8 text-right">
        <button onClick={() => onSaveGoals(editableGoals)} className="bg-magenta text-black font-bold py-3 px-6 rounded-lg hover:bg-opacity-80 transition glow-magenta">Save All Goals</button>
      </div>
    </>
  );
}

export default GoalSettingTab;


